import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {PhonenumberEntry} from '@app/models/phonenumber-entry';
import {QueueAgentModel, QueueDataModel} from '@app/models/queue-data.model';
import {ServiceNumberUtil} from '@app/models/service-number.util';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtManageOrganizationService} from '@app/services/gt-manage-organization.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {GtQueueService} from '@app/services/gt-queue.service';
import {InputDialogComponent, InputDialogData} from '@app/shared/input-dialog/input-dialog.component';
import {environment} from '@environment';
import {Skill} from '@gt-api';
import {TranslateService} from '@ngx-translate/core';
import {concat, Observable, of, Subject} from 'rxjs';
import {catchError, concatMap, tap} from 'rxjs/operators';

@Component({
  selector: 'gt-skill-editor',
  templateUrl: './skill-editor.component.html',
  styleUrls: ['./skill-editor.component.scss']
})
export class SkillEditorComponent implements OnInit, OnDestroy {
  @Output() back = new EventEmitter<void>();
  @Input() queueData: QueueDataModel;
  isAdmin = false;
  newAgent: QueueAgentModel;
  canEditActive = environment.hasEditAnswerGroupLogin;
  hasDragAndDrop = environment.hasDragAndDropSkillEditor;
  wrapupTime = environment.wrapupTime;
  noReplyTime = environment.noReplyTime;

  readonly callbackQueueCid = 39;
  readonly bothQueueCid = -1;

  dropdownSkillValues = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  private destroyed = new Subject<void>();

  constructor(
    private queueService: GtQueueService,
    private authenticationService: GtAuthenticationService,
    private manageService: GtManageOrganizationService,
    private notificationService: GtNotificationService,
    private dialog: MatDialog,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.authenticationService.isAdmin().subscribe(isAdmin => {
      this.isAdmin = isAdmin;
    });

    // Add values not in the standard not in the standard dropdown menu
    this.queueData.queueAgents.forEach(agent => {
      if (!this.wrapupTime.some(wt => wt === agent.wrapupTime)) {
        this.wrapupTime.push(agent.wrapupTime);
      }
      if (!this.noReplyTime.some(nrt => nrt === agent.noReplyTime)) {
        this.noReplyTime.push(agent.noReplyTime);
      }
    });

    this.wrapupTime.sort((a, b) => a - b);
    this.noReplyTime.sort((a, b) => a - b);
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  getAgentName(agent: QueueAgentModel): string {
    return agent.firstName ? agent.firstName + ' ' + agent.lastName : agent.lastName;
  }

  toggleActive(agent: QueueAgentModel) {
    if (this.isAdmin || agent.login === this.authenticationService.login) {
      agent.active = !agent.active;

      const skill = this.createSkillObj(agent);
      this.editSkill(agent, skill).subscribe();
    }
  }

  drop(event: CdkDragDrop<QueueAgentModel[]>) {
    if (this.hasDragAndDrop) {
      const list = this.queueData.queueAgents;
      if (this.canEdit(list[event.currentIndex])) {
        const b = list[event.currentIndex];
        if (!b) {
          console.log('invalid target index ' + event.currentIndex);
          return;
        }
        const previousAgent: QueueAgentModel = list.splice(event.previousIndex, 1)[0]; // Removes the element at previous index
        list.splice(event.currentIndex, 0, previousAgent); // Adds removed element at correct position in array

        const editSkill$ = list.map((agent, index) => {
          agent.skillLevel = index + 1; // skillLevel starts on 1, if it's 0, the backend forces it to 1. Without fixing the other
          return this.editSkill(agent, this.createSkillObj(agent));
        });
        concat(...editSkill$).subscribe();
      } else {
        this.error("You don't have the permission to change the agent's order");
      }
    }
  }

  selectChange(agent: QueueAgentModel) {
    this.editSkill(agent, this.createSkillObj(agent)).subscribe();
  }

  editSkill(queueAgent: QueueAgentModel, skill: Skill): Observable<unknown> {
    skill.queueCid = this.bothQueueCid; // Update all queues
    return this.queueService.editSkill(queueAgent.organization, queueAgent.login, skill).pipe(
      tap(() => {
        if (!this.hasDragAndDrop) {
          this.sortAgents();
        }
      }),
      catchError(error => {
        this.error(error);
        return of(null);
      }),
    );
  }

  initNewAgent() {
    this.newAgent = {
      noReplyTime: 20,
      organization: this.manageService.manageOname,
      skillLevel: environment.hasDragAndDropSkillEditor ? this.queueData.queueAgents.length + 1 : 10,
      wrapupTime: 0,
      active: false,
      state: 0,
      cid: this.queueData.cid,
      serviceNumber: this.queueData.serviceNumber
    };
  }

  removeAgent(queueAgent: QueueAgentModel) {
    const data: InputDialogData = {
      message: this.translateService.instant('skill_editor.question'),
      subheader: this.translateService.instant('skill_editor.remove_agent_verify'),
      showInput: false
    };
    this.dialog
      .open(InputDialogComponent, {
        disableClose: false,
        data
      })
      .afterClosed()
      .subscribe(ok => {
        if (ok) {
          this.queueService
            .removeSkill(queueAgent.organization, queueAgent.login, this.createSkillObj(queueAgent))
            .pipe(
              concatMap(() =>
                this.queueService.removeSkill(
                  queueAgent.organization,
                  queueAgent.login,
                  this.createSkillObj(queueAgent, this.callbackQueueCid)
                )
              )
            )
            .subscribe(
              () => {
                this.queueData.queueAgents.splice(this.queueData.queueAgents.indexOf(queueAgent), 1);
              },
              error => this.error(error)
            );
        }
      });
  }

  canEdit(agent: QueueAgentModel): boolean {
    return this.isAdmin || agent.login === this.authenticationService.login;
  }

  createSkillObj(queueAgent: QueueAgentModel, cid?: number): Skill {
    return {
      queueCid: cid ? cid : this.queueData.cid,
      serviceNumber: this.queueData.serviceNumber,
      deactivated: !queueAgent.active,
      wrapupTime: queueAgent.wrapupTime,
      noReplyTime: queueAgent.noReplyTime,
      skillLevel: queueAgent.skillLevel
    };
  }

  private error = err => {
    this.notificationService.customError(err);
  };

  private sortAgents() {
    this.queueData.queueAgents.sort((a, b) => {
      if (a.skillLevel === b.skillLevel) {
        if (this.getAgentName(a) < this.getAgentName(b)) {
          return -1;
        }
        if (this.getAgentName(a) > this.getAgentName(b)) {
          return 1;
        }
        return 0;
      }
      return a.skillLevel - b.skillLevel;
    });
  }

  newAgentAdded(newAgent: PhonenumberEntry) {
    this.newAgent.login = newAgent.login;
    this.newAgent.lastName = newAgent.name;
  }

  getQueueAgentStatusColor(queueAgent: QueueAgentModel): string {
    return ServiceNumberUtil.getQueueAgentStatusColor(queueAgent);
  }

  getQueueStatus(queueAgent: QueueAgentModel): string {
    return ServiceNumberUtil.getQueueStatus(queueAgent);
  }

  saveNewAgent(queueAgent: QueueAgentModel) {
    if (queueAgent && queueAgent.login) {
      this.queueService
        .addSkill(queueAgent.organization, queueAgent.login, this.createSkillObj(queueAgent))
        .pipe(
          concatMap(() =>
            this.queueService.addSkill(queueAgent.organization, queueAgent.login, this.createSkillObj(queueAgent, this.callbackQueueCid))
          )
        )
        .subscribe(
          () => {
            this.queueData.queueAgents.push(this.newAgent);
            this.newAgent = null;
            if (!this.hasDragAndDrop) {
              this.sortAgents();
            }
            this.notificationService.showMessage('skill_editor.add_agent');
          },
          error => this.error(error)
        );
    }
  }
}
